<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, inject, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import { coreServiceRegistry } from "@core/services/register"
import type { OwnerOutput } from "@core/composables"
import { doesOwnerEmailMatchConsentForShare, pceValidator } from "@core/helpers"
import type { Address } from "@types"

// ================================
// TYPES & PROPS
// ================================

/** Payload unique de l'emit exist (aligné PdlFormSearch, avec champs PCE) */
export type PceFormSearchExistPayload = {
    /** true si le PCE existe en base avec consentement valide (et DDA active si PCE), false sinon */
    exists: boolean
    /** Type de source */
    type: "pce"
    /** Numéro PCE saisi / vérifié */
    pce: string
    /** Contexte invitation (parcours sur invitation) */
    invitation?: boolean
    /** Présent quand exists === true : true si l'utilisateur a déjà accès à la source */
    consentementUser?: boolean
    /** Présent quand exists === true : id de la source existante */
    _id_source?: string
    /** Statut DDA : quand exists === true valeur API (ex. "Active") ; quand exists === false code d'erreur (not_found_source, etc.) */
    dda: string
    /** Date de début de récupération des données (optionnelle, peut être renseignée plus tard) */
    date_from_data?: string
    /**
     * true si email titulaire saisi = signataire/titulaire du consentement (parcours SHARE en invitation sans accès).
     */
    sameTitulaireEmail?: boolean
}

/** Réhydratation au retour depuis une étape aval (ex. BACK depuis signature) */
export type PceFormSearchRestore = {
    /** Numéro PCE (id source métier) */
    pce: string
    step: "verify" | "awaiting_date"
    /** Requis si step === awaiting_date */
    dateFromData?: string
    /** Requis si step === awaiting_date : même forme que pendingPayload avant Continuer */
    pending?: PceFormSearchExistPayload
}

type Props = {
    /** Adresse du bâtiment pour la recherche PCE (optionnel) */
    address?: Address | null
    /** Titulaire (particulier ou entreprise) pour la recherche GRDF/DDA (optionnel) */
    owner?: OwnerOutput
    /** Contexte invitation (parcours sur invitation) */
    invitation?: boolean
    /** État recherche à réappliquer depuis le contexte machine (retour arrière) */
    restore?: PceFormSearchRestore | null
}

const props = withDefaults(defineProps<Props>(), {
    address: undefined,
    owner: undefined,
    invitation: false,
    restore: undefined,
})

const { t } = useI18n({
    messages: {
        fr: {
            title: "Recherche du point de comptage et d'estimation (PCE)",
            placeholder: "Identifiant PCE",
            hint_pce_format_guide:
                "Saisissez le numéro PCE : 14 chiffres, ou les lettres GI suivies de 6 chiffres (ex. GI123456). Les espaces sont ignorés.",
            hint_pce_format_invalid:
                "Format non reconnu. Vérifiez votre saisie.",
            verify: "Vérifier",
            continue: "Continuer",
            back: "Retour",
            back_edit_pce: "Modifier le numéro du compteur",
            retry: "Réessayer",
            verify_loading: "Vérification en cours…",
            error_verify: "Une erreur est survenue lors de la vérification.",
            date_from_data_label: "Date de première donnée",
            date_from_data_placeholder: "Choisir une date",
            date_from_data_hint:
                "Date à partir de laquelle récupérer les données de consommation.",
        },
    },
})

const emit = defineEmits<{
    back: []
    exist: [payload: PceFormSearchExistPayload]
}>()

/** Espaces ignorés pour la validation et l’appel API */
function normalizePceSearchInput(raw: string): string {
    return raw.trim().replace(/\s+/g, "")
}

/** Valeur envoyée à l’API : PCE 14 chiffres tel quel, sinon GI en majuscules + 6 chiffres */
function toCanonicalPceId(normalized: string): string {
    if (/^gi\d{6}$/i.test(normalized)) {
        return `GI${normalized.slice(2)}`
    }
    return normalized
}

// ================================
// SERVICES
// ================================

const injectedServices = inject<{
    grdf?: typeof coreServiceRegistry.grdf
}>("services")
const grdfService = injectedServices?.grdf ?? coreServiceRegistry.grdf

// ================================
// STATE & FETCH
// ================================

const pceId = ref("")

/** Identifiant normalisé (trim, espaces retirés) pour valider et appeler l’API */
const normalizedPceId = computed(() => normalizePceSearchInput(pceId.value))

/** Format attendu : aligné sur `pceValidator` (14 chiffres ou GI + 6 chiffres en canonique) */
const isPceIdFormatValid = computed(() =>
    pceValidator(toCanonicalPceId(normalizedPceId.value))
)

/** true si l’utilisateur a saisi du texte mais le format est invalide */
const showPceFormatHint = computed(
    () => pceId.value.trim() !== "" && !isPceIdFormatValid.value
)

/** Message d’erreur sous le hint (BaseInputWrapper) */
const getPceIdsourceWrapperError = computed(() =>
    showPceFormatHint.value ? t("hint_pce_format_invalid") : ""
)

/** Étape courante : saisie PCE + vérifier, ou saisie date + continuer */
const step = ref<"verify" | "awaiting_date">("verify")

/** Payload mis en attente quand la vérification réussit (compteur absent ou consentement révoqué), avant saisie de date_from_data */
const pendingPayload = ref<PceFormSearchExistPayload | null>(null)

/** Date de première donnée saisie par l'utilisateur (format ISO YYYY-MM-DD pour l'emit) */
const dateFromData = ref<string>("")

watch(
    () => props.restore,
    (r) => {
        if (!r?.pce) return
        pceId.value = r.pce
        if (r.step === "awaiting_date" && r.pending && r.dateFromData) {
            step.value = "awaiting_date"
            pendingPayload.value = { ...r.pending }
            dateFromData.value = r.dateFromData
            return
        }
        step.value = "verify"
        pendingPayload.value = null
        dateFromData.value = ""
    },
    { immediate: true }
)

const { fetch, isFetching, hasError } = useFetchWrapper(async () => {
    const normalized = normalizedPceId.value
    const canonical = toCanonicalPceId(normalized)
    if (!normalized || !pceValidator(canonical)) return
    const pce = canonical
    const result = await grdfService.verifyPce({ idsource: pce })
    if ("notFound" in result) {
        pendingPayload.value = {
            exists: false,
            type: "pce",
            pce,
            dda: result.code,
            invitation: props.invitation,
        }
        dateFromData.value = ""
        step.value = "awaiting_date"
        return
    }
    const hasAccess = result.hasAccess ?? false
    if (hasAccess) {
        emit("exist", {
            exists: true,
            type: "pce",
            pce,
            consentementUser: true,
            _id_source: result.source?._id,
            dda: result.dda,
            invitation: props.invitation,
        })
        return
    }
    pendingPayload.value = {
        exists: true,
        type: "pce",
        pce,
        consentementUser: false,
        _id_source: result.source?._id,
        dda: result.dda,
        invitation: props.invitation,
        sameTitulaireEmail: doesOwnerEmailMatchConsentForShare(
            props.owner,
            result.consentement
        ),
    }
    dateFromData.value = ""
    step.value = "awaiting_date"
})

// ================================
// INTERACTIONS
// ================================

function onBack(): void {
    if (step.value === "awaiting_date") {
        step.value = "verify"
        pendingPayload.value = null
        dateFromData.value = ""
        return
    }
    emit("back")
}

function onVerify(): void {
    fetch()
}

function onRetry(): void {
    fetch()
}

/** Émet exist avec le payload en attente + date_from_data, puis réinitialise l’étape. */
function onContinue(): void {
    const payload = pendingPayload.value
    if (!payload || !dateFromData.value) return
    emit("exist", {
        ...payload,
        date_from_data: dateFromData.value,
    })
    step.value = "verify"
    pendingPayload.value = null
    dateFromData.value = ""
}

/** Convertit la valeur du DatePicker (Date | string) en chaîne ISO YYYY-MM-DD. */
function onDateFromDataChange(value: Date | string | null): void {
    if (value == null) {
        dateFromData.value = ""
        return
    }
    const d = typeof value === "string" ? new Date(value) : value
    if (Number.isNaN(d.getTime())) {
        dateFromData.value = ""
        return
    }
    dateFromData.value = d.toISOString().slice(0, 10)
}
</script>

<template>
    <div class="pce-form-search flex flex-col gap-4">
        <header class="w-full text-center flex flex-col gap-4">
            <h2 class="text-lg font-semibold text-gray-700 text-center">
                {{ t("title") }}
            </h2>
        </header>

        <BaseInputWrapper
            input-id="pce-form-search-idsource"
            :label="t('placeholder')"
            :hint="t('hint_pce_format_guide')"
            :error="getPceIdsourceWrapperError"
            class="w-full"
        >
            <BaseInputText
                v-model="pceId"
                input-id="pce-form-search-idsource"
                fluid
                :placeholder="t('placeholder')"
                :disabled="isFetching || step === 'awaiting_date'"
                :invalid="showPceFormatHint"
                autocomplete="off"
            />
        </BaseInputWrapper>

        <!-- Étape date de première donnée (après vérification OK : compteur absent ou consentement révoqué) -->
        <template v-if="step === 'awaiting_date'">
            <BaseInputWrapper
                unfloat
                input-id="pce-date-from-data"
                :label="t('date_from_data_label')"
                :hint="t('date_from_data_hint')"
            >
                <BaseDatePicker
                    :model-value="dateFromData ? new Date(dateFromData) : null"
                    @update:model-value="onDateFromDataChange"
                    date-format="dd/mm/yy"
                    show-icon
                    :max-date="new Date()"
                    icon-display="input"
                    input-id="pce-date-from-data"
                    :placeholder="t('date_from_data_placeholder')"
                />
            </BaseInputWrapper>
        </template>

        <div
            class="flex flex-col-reverse gap-3 md:flex-row md:flex-wrap md:justify-between"
        >
            <BaseButton
                :label="
                    step === 'awaiting_date' ? t('back_edit_pce') : t('back')
                "
                severity="secondary"
                @click="onBack"
                :disabled="isFetching"
                class="min-w-[10rem]"
            />
            <template v-if="step === 'awaiting_date'">
                <BaseButton
                    :label="t('continue')"
                    :disabled="!dateFromData"
                    @click="onContinue"
                    class="min-w-[10rem]"
                />
            </template>
            <template v-else>
                <BaseButton
                    :label="hasError ? t('retry') : t('verify')"
                    :loading="isFetching"
                    :disabled="!isPceIdFormatValid || isFetching"
                    @click="hasError ? onRetry() : onVerify()"
                    class="min-w-[10rem]"
                />
            </template>
        </div>
        <BaseMessage v-if="hasError" severity="error">
            {{ t("error_verify") }}
        </BaseMessage>
    </div>
</template>
